import * as React from 'react'
import * as t from './types'
import styled from 'styled-components'
import theme from 'theme'

import ProductWidget from 'theme/molecules/ProductWidget'

import EecTracking from 'theme/atoms/EecTracking'
import Button from 'theme/atoms/Button'
import useTranslations from 'hooks/useTranslations'
import withTranslations from './withTranslations'

export function ProductListing(props: t.Props) {
  const t = useTranslations<'organisms-product-listing'>()
  const hits = props.context
  const scrollRef = React.useRef<null | HTMLObjectElement>(null)
  const list_id = typeof window !== 'undefined' ? window.location.href : ''

  const [isExpanded, setIsExpanded] = React.useState(false)

  const displayedItems =
    isExpanded || !props.limitRows
      ? hits.data.length
      : parseInt(props.initialRows, 10) * 5

  return (
    <EecTracking
      config={props.eecTracking}
      gridArea={props.gridArea}
      threshold={1}
    >
      <Wrapper
        className="ProductList"
        data-cy-ctx="organisms/ProductListing"
        ref={scrollRef}
      >
        <h2 className="title">{props.title}</h2>
        <div className="right">
          {hits && hits.data.length > 0 ? (
            <div className="products listing" data-cy-state="show-products">
              {hits.data.map(
                (hit, idx) =>
                  idx < displayedItems && (
                    <ProductWidget
                      key={hit.sku}
                      product={hit}
                      listname={props.title}
                      listPosition={1}
                      listid={list_id}
                    />
                  )
              )}
            </div>
          ) : (
            <div className="no-result listing">
              <div className="info-message">{t('no_products')}</div>
            </div>
          )}
        </div>
        {!isExpanded && displayedItems < hits.data.length && (
          <div
            className="more-button"
            data-cy-state="show-expand-product-list-button"
          >
            <Button
              onClick={() => setIsExpanded(true)}
              variation="primary"
              data-cy-handle="expand-product-list"
            >
              {t('expand_products_button_label')}
            </Button>
            {t('hidden_items_label', {
              DISPLAYED: displayedItems,
              TOTAL: hits.data.length
            })}
          </div>
        )}
      </Wrapper>
    </EecTracking>
  )
}

export default withTranslations(ProductListing)

// prevent unnecessary linebreaks to avoid lint error
const MS = theme.ms

const Wrapper = styled.div`
  width: 100%;
  display: block;
  flex-direction: column;
  @media (min-width: ${MS.LG}px) {
    flex-direction: row;
  }
  > .title {
    ${theme.ty('rc-2xl')}
    @media (min-width: ${theme.ms.MD}px) {
      ${theme.ty('rc-3xl')}
    }
  }
  > .right {
    width: 100%;
    > .listing {
      margin-top: ${theme.spacing('ml')};
      @media (min-width: ${MS.LG}px) {
        margin-top: ${theme.spacing('l')};
      }
    }
    > .no-result {
      text-align: center;
      margin-top: ${theme.spacing('xxl')};
      > div {
        &.info-message {
          ${theme.ty('rc-s')}
        }
      }
    }
    > .products {
      width: 100%;
      display: grid;
      grid-template-columns: 1fr 1fr;
      grid-column-gap: ${theme.spacing('m')};
      grid-row-gap: ${theme.spacing('xl')};
      position: relative;
      @media (min-width: ${MS.SM}px) {
        grid-template-columns: 1fr 1fr 1fr;
        grid-column-gap: ${theme.spacing('m')};
        grid-row-gap: ${theme.spacing('xl')};
      }
      @media (min-width: ${MS.LG}px) {
        grid-template-columns: 1fr 1fr 1fr 1fr;
        grid-column-gap: ${theme.spacing('m')};
        grid-row-gap: calc(${theme.spacing('l')} * 2);
      }
      @media (min-width: ${MS.XL}px) {
        grid-template-columns: 1fr 1fr 1fr 1fr 1fr;
      }
      /** TODO: DO NOT ACCESS INNER IMPLEMENTATION!!!! */
      > .ProductWidget {
        > .ImgBox {
          padding: 0 ${theme.spacing('s')};
          @media (min-width: ${MS.SM}px) {
            padding: 0 ${theme.spacing('m')};
          }
        }
      }
      /** calc vertical line */
      @media (min-width: ${MS.XS}px) and (max-width: ${MS.SM - 1}px) {
        > .ProductWidget:nth-child(2n + 3):before {
          content: '';
          height: 1px;
          width: 100%;
          left: 0;
          margin-top: calc(${theme.spacing('l')} * -1);
          background: ${theme.colors.shade.b6};
          position: absolute;
          cursor: default;
        }
      }
      @media (min-width: ${MS.SM}px) and (max-width: ${MS.LG - 1}px) {
        > .ProductWidget:nth-child(3n + 4):before {
          content: '';
          height: 1px;
          width: 100%;
          left: 0;
          margin-top: calc(${theme.spacing('l')} * -1);
          background: ${theme.colors.shade.b6};
          position: absolute;
          cursor: default;
        }
      }
      @media (min-width: ${MS.LG}px) and (max-width: ${MS.XL - 1}px) {
        > .ProductWidget:nth-child(4n + 5):before {
          content: '';
          height: 1px;
          width: 100%;
          left: 0;
          margin-top: calc(${theme.spacing('l')} * -1);
          background: ${theme.colors.shade.b6};
          position: absolute;
          cursor: default;
        }
      }
      @media (min-width: ${MS.XL}px) {
        > .ProductWidget:nth-child(5n + 6):before {
          content: '';
          height: 1px;
          width: 100%;
          left: 0;
          margin-top: calc(${theme.spacing('l')} * -1);
          background: ${theme.colors.shade.b6};
          position: absolute;
          cursor: default;
        }
      }
    }
  }

  > .more-button {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    margin-top: ${theme.spacing('l')};
    > p {
      margin-left: ${theme.spacing('s')};
      ${theme.ty('r-s', null, 'r-base')}
      margin-bottom: 0;
    }
    > span {
      margin-top: ${theme.spacing('s')};
    }
  }
`
